import React from 'react';
import { NavLink } from 'react-router-dom';
import { Sym, IconButton, Avatar } from './m3';
import wordmark from '../assets/grovision-wordmark.png';
import mark from '../assets/grovision-mark.png';

/* ============================================================================
 * AdminSidebar — grouped navigation rail (collapsible).
 * Routing is react-router; each item is a NavLink so the URL stays the source
 * of truth. Badges (pending members / active warnings) are passed in.
 * ========================================================================= */

export interface NavItem {
  key: string;
  to: string;
  icon: string;
  label: string;
  end?: boolean;
  adminOnly?: boolean;
}
export interface NavGroup {
  title: string;
  items: NavItem[];
}

export const ADMIN_NAV: NavGroup[] = [
  {
    title: '모니터링',
    items: [
      { key: 'dashboard', to: '/', icon: 'space_dashboard', label: '통합 모니터링', end: true },
      { key: 'data', to: '/data', icon: 'analytics', label: '데이터 조회' },
      { key: 'growth', to: '/growth', icon: 'potted_plant', label: '생육 데이터' },
      { key: 'map', to: '/map', icon: 'map', label: '지도' },
      { key: 'alerts', to: '/alerts', icon: 'notifications', label: '알림' },
    ],
  },
  {
    title: '관리',
    items: [
      { key: 'members', to: '/members', icon: 'group', label: '회원관리', adminOnly: true },
      { key: 'managers', to: '/managers', icon: 'shield_person', label: '담당자관리', adminOnly: true },
      { key: 'farms', to: '/farms', icon: 'agriculture', label: '농장', adminOnly: true },
      { key: 'devices', to: '/devices', icon: 'sensors', label: '장비', adminOnly: true },
      { key: 'models', to: '/models', icon: 'memory', label: '장비모델', adminOnly: true },
    ],
  },
  {
    title: '소통',
    items: [
      { key: 'notice', to: '/notice', icon: 'campaign', label: '공지', adminOnly: true },
      { key: 'messages', to: '/messages', icon: 'sms', label: '메시지발송', adminOnly: true },
    ],
  },
  {
    title: '시스템',
    items: [
      { key: 'mqtt', to: '/mqtt', icon: 'hub', label: 'MQTT 모니터링', adminOnly: true },
    ],
  },
];

export interface AdminSidebarProps {
  collapsed?: boolean;
  dark?: boolean;
  onToggleDark?: () => void;
  onLogout?: () => void;
  isAdmin?: boolean;
  userName?: string;
  userEmail?: string;
  roleLabel?: string;
  badges?: Record<string, number>;
}

export function AdminSidebar({
  collapsed = false,
  dark = false,
  onToggleDark,
  onLogout,
  isAdmin = true,
  userName = '관리자',
  userEmail = '',
  roleLabel = '관리자',
  badges = {},
}: AdminSidebarProps): React.ReactElement {
  const width = collapsed ? 76 : 244;
  return (
    <aside
      style={{
        width,
        flexShrink: 0,
        height: '100vh',
        background: 'var(--md-sys-color-surface-container-low)',
        borderRight: '1px solid var(--md-sys-color-outline-variant)',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 200ms cubic-bezier(.2,0,0,1)',
        overflow: 'hidden',
      }}
    >
      {/* Brand */}
      <div
        style={{
          height: 72,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: collapsed ? '0' : '0 20px',
          justifyContent: collapsed ? 'center' : 'flex-start',
          flexShrink: 0,
        }}
      >
        {collapsed ? (
          <img src={mark} alt="GroVision" style={{ width: 38, height: 38, objectFit: 'contain' }} />
        ) : (
          <div>
            <img src={wordmark} alt="GroVision" style={{ height: 30, objectFit: 'contain', display: 'block' }} />
            <div style={{ font: '600 11px/1 var(--gs-font-kr)', letterSpacing: 1, color: 'var(--md-sys-color-primary)', marginTop: 4 }}>
              환경·생육 모니터링
            </div>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="gs-scroll-hide" style={{ flex: 1, overflowY: 'auto', padding: '6px 12px 12px' }}>
        {ADMIN_NAV.map((group) => {
          const items = group.items.filter((it) => !it.adminOnly || isAdmin);
          if (items.length === 0) return null;
          return (
            <div key={group.title} style={{ marginBottom: 14 }}>
              {!collapsed && (
                <div
                  style={{
                    font: '600 11px/1 var(--gs-font-kr)',
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                    color: 'var(--md-sys-color-on-surface-variant)',
                    padding: '8px 12px 6px',
                  }}
                >
                  {group.title}
                </div>
              )}
              {items.map((it) => (
                <NavLink key={it.key} to={it.to} end={it.end} title={collapsed ? it.label : undefined} style={{ display: 'block' }}>
                  {({ isActive }) => (
                    <div
                      className="gs-navitem"
                      style={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: collapsed ? '11px 0' : '11px 12px',
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        borderRadius: 12,
                        marginBottom: 2,
                        background: isActive ? 'var(--md-sys-color-secondary-container)' : 'transparent',
                        color: isActive
                          ? 'var(--md-sys-color-on-secondary-container)'
                          : 'var(--md-sys-color-on-surface-variant)',
                        font: `${isActive ? 600 : 500} 14px/1 var(--gs-font-kr)`,
                        transition: 'background 150ms',
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive) e.currentTarget.style.background = 'var(--md-sys-color-surface-container-high)';
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <Sym name={it.icon} size={21} fill={isActive ? 1 : 0} />
                      {!collapsed && <span style={{ flex: 1 }}>{it.label}</span>}
                      {badges[it.key] ? (
                        <Badge n={badges[it.key]} warn={it.key === 'alerts'} collapsed={collapsed} />
                      ) : null}
                    </div>
                  )}
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div
        style={{
          borderTop: '1px solid var(--md-sys-color-outline-variant)',
          padding: collapsed ? '12px 0' : '12px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          justifyContent: collapsed ? 'center' : 'flex-start',
          flexShrink: 0,
        }}
      >
        <Avatar name={userName} size={36} color="var(--gs-secondary-90)" fg="var(--gs-secondary-30)" />
        {!collapsed && (
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ font: '600 13px/1.2 var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {userName} · {roleLabel}
            </div>
            {userEmail && (
              <div style={{ font: '400 11px/1.2 var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {userEmail}
              </div>
            )}
          </div>
        )}
        {!collapsed && (
          <>
            <IconButton icon={dark ? 'light_mode' : 'dark_mode'} onClick={onToggleDark} title="테마 전환" size={20} />
            {onLogout && <IconButton icon="logout" onClick={onLogout} title="로그아웃" size={20} />}
          </>
        )}
      </div>
    </aside>
  );
}

function Badge({ n, warn, collapsed }: { n: number; warn?: boolean; collapsed?: boolean }): React.ReactElement {
  return (
    <span
      className="gs-num"
      style={{
        position: collapsed ? 'absolute' : 'static',
        top: collapsed ? 6 : undefined,
        right: collapsed ? 14 : undefined,
        minWidth: 20,
        height: 20,
        padding: '0 6px',
        borderRadius: 9999,
        background: warn ? 'var(--gs-warning)' : 'var(--md-sys-color-primary)',
        color: '#fff',
        font: '600 11px/20px var(--gs-font-kr)',
        textAlign: 'center',
      }}
    >
      {n}
    </span>
  );
}
